// FLIP helpers for the Explore popup ⇄ inline land-detail shared-element morph.
//
// The map popup card (LandCardPopup) and the detail hero (#image-block in the
// embedded LandPage) are different components that never coexist, so their
// geometry can't be transitioned directly. Instead the outgoing side stashes
// its bounding rect just before the swap, and the incoming side animates FROM
// that rect TO its own natural position with the Web Animations API. The morph
// is transform-only (translate3d + scale) so it stays on the compositor.

export const MORPH_TO_DETAIL_KEY = "eco:morph:popup-to-detail";
export const MORPH_TO_POPUP_KEY = "eco:morph:detail-to-popup";

// A stash is only valid for the mount that immediately follows it — anything
// older (abandoned open, refresh, back/forward nav) must not replay a stale
// morph from coordinates that no longer mean anything.
const MAX_AGE_MS = 3000;

export type MorphRect = {
  id: string;
  t: number;
  left: number;
  top: number;
  width: number;
  height: number;
};

export const stashMorphRect = (key: string, id: string | number | null | undefined, rect: DOMRect): void => {
  if (id == null || !rect.width || !rect.height) {
    return;
  }
  try {
    const payload: MorphRect = {
      id: String(id),
      t: Date.now(),
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
    sessionStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // Storage unavailable (private mode, quota) — the morph just won't play.
  }
};

// Read-and-consume the stashed rect. Returns null (and plays nothing) unless
// the stash exists, matches the land being opened, and is fresh.
export const takeMorphRect = (key: string, id: string | number | null | undefined): MorphRect | null => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    sessionStorage.removeItem(key);
    const stash = JSON.parse(raw) as MorphRect;
    if (id == null || stash.id !== String(id)) {
      return null;
    }
    if (Date.now() - stash.t > MAX_AGE_MS) {
      return null;
    }
    return stash;
  } catch {
    return null;
  }
};

// FLIP invert+play: place `el` visually at the stashed rect, then release it to
// its natural position. Compositor-only props; bails (returns null) when the
// Web Animations API is missing or the user prefers reduced motion, so callers
// can fall back to their non-morph entrance.
export const playMorphFromRect = (el: HTMLElement, from: MorphRect, duration = 650): Animation | null => {
  if (typeof el.animate !== "function") {
    return null;
  }
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    return null;
  }
  const to = el.getBoundingClientRect();
  if (!to.width || !to.height) {
    return null;
  }
  const dx = from.left - to.left;
  const dy = from.top - to.top;
  const sx = from.width / to.width;
  const sy = from.height / to.height;
  if (!Number.isFinite(dx + dy + sx + sy)) {
    return null;
  }
  const animation = el.animate(
    [
      {
        transformOrigin: "top left",
        transform: `translate3d(${dx}px, ${dy}px, 0) scale(${sx}, ${sy})`,
      },
      { transformOrigin: "top left", transform: "none" },
    ],
    { duration, easing: "cubic-bezier(0.22, 1, 0.36, 1)" }
  );
  // Surgical will-change: promote only for the duration of the morph.
  el.style.willChange = "transform";
  const clearWillChange = () => {
    el.style.willChange = "";
  };
  animation.addEventListener("finish", clearWillChange, { once: true });
  animation.addEventListener("cancel", clearWillChange, { once: true });
  return animation;
};
